import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { looksApi } from '../api/looks.js';
import { useBookLooks } from './useBookLooks.js';
import { userErrorMessage } from '../api/errors.js';
import { useI18n } from '../i18n/context.js';
import { Select } from '../components/Select.js';

// Keep the per-spread outfit editor available to restore, but hide it for now.
const SHOW_SCENE_LOOK_ASSIGNMENTS = false;

interface LooksPanelProps {
  bookId: string;
  previewOnly: boolean;
  readOnly?: boolean;
  artworkActive?: boolean;
}

export function LooksPanel(props: LooksPanelProps) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const query = useBookLooks(props.bookId);
  const data = query.data;
  if (data && !data.scenes.length) return null;
  // Everything here runs by itself: the closed block speaks only while it is busy or
  // when something needs the owner's hand.
  const references = data ? [...data.items, ...(data.subjects ?? [])] : [];
  const planning = data?.planStatus === 'queued' || data?.planStatus === 'running';
  const referencesFailed = references.some((r) => r.status === 'failed');
  const drawing = references.some((r) => r.status === 'queued' || r.status === 'running');
  const preparing =
    !props.readOnly && (planning || (data?.planStatus === 'ready' && !referencesFailed && drawing));
  const summary = props.readOnly
    ? null
    : planning
      ? t.looks.planning
      : data?.planStatus === 'failed'
        ? t.looks.planFailed
        : data?.planStatus === 'stale'
          ? t.looks.stale
          : referencesFailed
            ? t.looks.referencesFailed
            : drawing
              ? t.looks.drawingReferences
              : null;
  return (
    <div className="grid gap-4">
      {preparing && summary ? <LooksPreparation message={summary} /> : null}
      <section className="kitup-card p-4">
        <button
          type="button"
          className="flex w-full flex-wrap items-center justify-between gap-2 text-left font-semibold"
          aria-expanded={expanded}
          aria-controls={`visual-settings-${props.bookId}`}
          onClick={() => setExpanded(!expanded)}
        >
          <span>{t.looks.advanced}</span>
          <span aria-hidden="true">{expanded ? '−' : '+'}</span>
        </button>
        {!expanded && query.isError ? (
          <p role="alert" className="mt-2 text-sm text-red-700">
            {userErrorMessage(query.error, t)}
          </p>
        ) : null}
        {!expanded && !preparing && summary ? (
          <p role="status" className="mt-2 text-sm text-[var(--kitup-color-muted)]">
            {summary}
          </p>
        ) : null}
        {expanded ? (
          <div id={`visual-settings-${props.bookId}`} className="mt-4">
            <LooksPanelContent {...props} />
          </div>
        ) : null}
      </section>
    </div>
  );
}

function LooksPreparation({ message }: { readonly message: string }) {
  const { t } = useI18n();
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="grid justify-items-center gap-5 rounded-2xl border border-[var(--kitup-color-accent)]/30 bg-[var(--kitup-color-accent)]/5 px-5 py-8 text-center sm:px-8 sm:py-10"
    >
      <svg
        viewBox="0 0 48 48"
        fill="none"
        aria-hidden="true"
        className="h-14 w-14 animate-spin text-[var(--kitup-color-accent)] motion-reduce:animate-none"
      >
        <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" opacity="0.18" />
        <path
          d="M24 4a20 20 0 0 1 20 20"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
      <div className="max-w-xl">
        <h2 className="text-xl font-semibold">{t.looks.preparingTitle}</h2>
        <p className="mt-2 font-medium">{message}</p>
        <p className="mt-2 text-sm text-[var(--kitup-color-muted)]">{t.looks.preparingHint}</p>
      </div>
    </div>
  );
}

function LooksPanelContent({
  bookId,
  previewOnly,
  readOnly = false,
  artworkActive = false,
}: LooksPanelProps) {
  const { t } = useI18n();
  const query = useBookLooks(bookId);
  const client = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guidance, setGuidance] = useState('');
  const data = query.data;
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['book-looks', bookId] }),
      client.invalidateQueries({ queryKey: ['book-illustrations', bookId] }),
      client.invalidateQueries({ queryKey: ['book', bookId] }),
    ]);
  const run = async (action: () => Promise<unknown>) => {
    setPending(true);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (e) {
      setError(userErrorMessage(e, t));
    } finally {
      setPending(false);
    }
  };
  if (query.isPending) return <p>{t.common.loading}</p>;
  if (query.isError) return <p role="alert">{userErrorMessage(query.error, t)}</p>;
  if (!data || !data.scenes.length) return null;
  const deferred = (sceneIds: readonly string[]) =>
    previewOnly && !sceneIds.includes(data.scenes[0]!.id);
  const active =
    data.planStatus === 'queued' ||
    data.planStatus === 'running' ||
    [...data.items, ...(data.subjects ?? [])].some(
      (l) => l.status === 'queued' || l.status === 'running',
    );
  return (
    <section className="grid gap-4" aria-label={t.looks.title}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold">{t.looks.title}</h2>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.looks.description}</p>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.looks.redoCost}</p>
        </div>
      </div>
      {data.planStatus === 'failed' || data.planStatus === 'stale' ? (
        <p role="alert">{data.planStatus === 'stale' ? t.looks.stale : t.looks.planFailed}</p>
      ) : null}
      {error ? <p role="alert">{error}</p> : null}
      {!readOnly ? (
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => looksApi.plan(bookId, guidance));
          }}
        >
          <label className="grid gap-1 text-sm">
            {t.looks.guidance}
            <textarea
              className="kitup-input"
              value={guidance}
              maxLength={2000}
              onChange={(e) => setGuidance(e.target.value)}
              placeholder={t.looks.guidancePlaceholder}
            />
          </label>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.looks.replanHint}</p>
          <button
            className="kitup-secondary-button justify-self-start"
            disabled={pending || active || artworkActive}
            type="submit"
          >
            {data.planStatus === 'none' ? t.looks.plan : t.looks.replan}
          </button>
        </form>
      ) : null}
      {data.items.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((look) => (
            <article
              key={look.id}
              className="grid content-start gap-2 rounded-xl border border-[var(--kitup-color-border)] p-3"
            >
              {look.imageUrl ? (
                <img
                  src={look.imageUrl}
                  alt={`${look.characterName}: ${look.name}`}
                  className="aspect-[3/2] w-full rounded-lg object-contain"
                />
              ) : (
                <div className="grid aspect-[3/2] place-items-center rounded-lg bg-[var(--kitup-color-surface-soft)] text-sm">
                  {t.looks.noImage}
                </div>
              )}
              <h3 className="font-semibold">
                {look.characterName} · {look.name}
              </h3>
              <p className="text-sm">
                {look.isBase ? (
                  t.looks.base
                ) : deferred(look.sceneIds) ? (
                  <DeferredReferenceNotice />
                ) : look.approved ? (
                  t.looks.approved
                ) : (
                  t.looks.needsApproval
                )}
              </p>
              <p className="text-sm text-[var(--kitup-color-muted)]">
                {t.looks.spreads(
                  look.sceneIds
                    .map((id) => (data.scenes.find((s) => s.id === id)?.index ?? 0) + 1)
                    .join(', '),
                )}
              </p>
              {look.status === 'queued' || look.status === 'running' ? (
                <p role="status">{t.looks.generating}</p>
              ) : null}
              {look.status === 'failed' ? <p role="alert">{t.looks.generationFailed}</p> : null}
              {!readOnly && !look.isBase && !deferred(look.sceneIds) ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="kitup-secondary-button"
                    disabled={pending || active || data.planStatus !== 'ready'}
                    onClick={() => void run(() => looksApi.generate(bookId, look.id))}
                  >
                    {look.imageUrl ? t.looks.regenerate : t.looks.generate}
                  </button>
                  {!look.approved && look.selectedVariantId ? (
                    <button
                      type="button"
                      className="kitup-primary-button"
                      disabled={pending || active || data.planStatus !== 'ready'}
                      onClick={() => void run(() => looksApi.approve(bookId, look.id))}
                    >
                      {t.looks.approve}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {look.variants.length > 1 ? (
                <div className="flex gap-2">
                  {look.variants.map((v) => (
                    <button
                      type="button"
                      key={v.id}
                      disabled={
                        readOnly ||
                        deferred(look.sceneIds) ||
                        pending ||
                        active ||
                        data.planStatus !== 'ready'
                      }
                      className={`w-20 rounded-lg border-2 ${v.id === look.selectedVariantId ? 'border-[var(--kitup-color-accent)]' : 'border-transparent'}`}
                      aria-label={t.looks.chooseVariant}
                      onClick={() => void run(() => looksApi.approve(bookId, look.id, v.id))}
                    >
                      <img src={v.url} alt="" className="aspect-[3/2] object-contain" />
                    </button>
                  ))}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
      {SHOW_SCENE_LOOK_ASSIGNMENTS && data.items.length > 0 ? (
        <div className="grid gap-3">
          {data.scenes.map((scene) => (
            <div key={scene.id} className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium">{t.story.manual.spread(scene.index + 1)}</span>
              {scene.characterIds.map((characterId) => {
                const looks = data.items.filter((l) => l.characterId === characterId);
                return (
                  <label key={characterId} className="flex items-center gap-2 text-sm">
                    {looks[0]?.characterName}
                    <Select
                      disabled={readOnly || pending || active || data.planStatus !== 'ready'}
                      value={looks.find((l) => l.sceneIds.includes(scene.id))?.id ?? ''}
                      options={looks.map((l) => ({ value: l.id, label: l.name }))}
                      onChange={(lookId) =>
                        void run(() => looksApi.assign(bookId, scene.id, characterId, lookId))
                      }
                    />
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
      {(data.subjects ?? []).length > 0 ? (
        <section className="grid gap-3" aria-label={t.looks.subjectsTitle}>
          <h3 className="font-semibold">{t.looks.subjectsTitle}</h3>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.looks.subjectsHint}</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.subjects.map((subject) => (
              <article
                key={subject.id}
                className="grid content-start gap-2 rounded-xl border border-[var(--kitup-color-border)] p-3"
              >
                {subject.imageUrl ? (
                  <img
                    src={subject.imageUrl}
                    alt={subject.name}
                    className="aspect-[3/2] w-full rounded-lg object-contain"
                  />
                ) : (
                  <p className="text-sm">{t.looks.noReference}</p>
                )}
                <h4 className="font-semibold">{subject.name}</h4>
                <p className="text-sm">
                  {t.looks.subjectKinds[subject.kind]} ·{' '}
                  {deferred(subject.sceneIds) ? (
                    <DeferredReferenceNotice />
                  ) : subject.approved ? (
                    t.looks.approved
                  ) : (
                    t.looks.needsApproval
                  )}
                </p>
                <p className="text-sm text-[var(--kitup-color-muted)]">
                  {t.looks.spreads(
                    subject.sceneIds
                      .map((id) => (data.scenes.find((s) => s.id === id)?.index ?? 0) + 1)
                      .join(', '),
                  )}
                </p>
                {subject.status === 'queued' || subject.status === 'running' ? (
                  <p role="status">{t.looks.drawingReference}</p>
                ) : null}
                {subject.status === 'failed' ? <p role="alert">{t.looks.referenceFailed}</p> : null}
                {!readOnly && !deferred(subject.sceneIds) ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="kitup-secondary-button"
                      disabled={pending || active || artworkActive || data.planStatus !== 'ready'}
                      onClick={() => void run(() => looksApi.generateSubject(bookId, subject.id))}
                    >
                      {subject.imageUrl ? t.looks.regenerate : t.looks.generateReference}
                    </button>
                    {!subject.approved && subject.selectedVariantId ? (
                      <button
                        type="button"
                        className="kitup-primary-button"
                        disabled={pending || active || artworkActive || data.planStatus !== 'ready'}
                        onClick={() => void run(() => looksApi.approveSubject(bookId, subject.id))}
                      >
                        {t.looks.approve}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {subject.variants.length > 1 ? (
                  <div className="flex gap-2">
                    {subject.variants.map((variant) => (
                      <button
                        key={variant.id}
                        type="button"
                        className={`w-20 rounded-lg border-2 ${variant.id === subject.selectedVariantId ? 'border-[var(--kitup-color-accent)]' : 'border-transparent'}`}
                        aria-label={t.looks.chooseVariant}
                        disabled={
                          readOnly ||
                          deferred(subject.sceneIds) ||
                          pending ||
                          active ||
                          artworkActive ||
                          data.planStatus !== 'ready'
                        }
                        onClick={() =>
                          void run(() => looksApi.approveSubject(bookId, subject.id, variant.id))
                        }
                      >
                        <img src={variant.url} alt="" className="aspect-[3/2] object-contain" />
                      </button>
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}
      {(data.catalogItems ?? []).length > 0 ? (
        <section className="grid gap-3" aria-label={t.looks.catalogTitle}>
          <h3 className="font-semibold">{t.looks.catalogTitle}</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.catalogItems.map((item) => (
              <article
                key={`${item.kind}:${item.id}`}
                className="grid gap-2 rounded-xl border border-[var(--kitup-color-border)] p-3"
              >
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="aspect-[3/2] w-full rounded-lg object-contain"
                  />
                ) : null}
                <h4 className="font-semibold">{item.name}</h4>
                <p className="text-sm">
                  {item.approved ? t.looks.fromLibrary : t.looks.libraryReferenceNeeded}
                </p>
                <p className="text-sm">
                  {t.looks.spreads(
                    item.sceneIds
                      .map((id) => (data.scenes.find((s) => s.id === id)?.index ?? 0) + 1)
                      .join(', '),
                  )}
                </p>
                <a href={item.href} className="text-sm underline">
                  {t.looks.openLibraryItem}
                </a>
              </article>
            ))}
          </div>
        </section>
      ) : null}
      {!readOnly && data.ready ? (
        <button
          type="button"
          className="kitup-primary-button justify-self-start"
          disabled={pending || active || artworkActive}
          onClick={() => void run(() => looksApi.continue(bookId))}
        >
          {t.looks.continue}
        </button>
      ) : null}
      {[...data.items, ...(data.subjects ?? [])].some((r) => r.status === 'failed') ? (
        <p role="alert" className="text-sm">
          {t.looks.referencesFailed}
        </p>
      ) : null}
    </section>
  );
}

function DeferredReferenceNotice() {
  const { t } = useI18n();
  const hintId = useId();
  return (
    <span
      tabIndex={0}
      aria-describedby={hintId}
      className="group relative inline-block cursor-help text-[var(--kitup-color-muted)] underline decoration-dotted underline-offset-4"
    >
      {t.looks.afterPayment}
      <span
        id={hintId}
        role="tooltip"
        className="pointer-events-none invisible absolute bottom-full left-0 z-10 mb-2 w-64 rounded-lg border border-[var(--kitup-color-border)] bg-[var(--kitup-color-paper)] p-3 text-sm font-normal text-[var(--kitup-color-ink)] shadow-lg group-hover:visible group-focus:visible"
      >
        {t.looks.drawAfterPayment}
      </span>
    </span>
  );
}
